import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import BioPage from './pages/BioPage';
import BioDashboard from './pages/BioDashboard';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CollabOrderPage from './pages/CollabOrderPage';
import UnsubscribePage from './pages/UnsubscribePage';
import EmbedTestPage from './pages/EmbedTestPage';
import ShoutOutProfilePage from './pages/ShoutOutProfilePage';
import AccountPage from './pages/AccountPage';
import SupportPage from './pages/SupportPage';
import MessagePage from './pages/MessagePage';
import StartPage from './pages/StartPage';

// The creator workspace. This IS /dashboard now — the redesign replaced the
// original on 2026-08-12 after a parity audit against it. Still lazy: it is a
// large bundle and the public fan pages, which are most of this app's traffic,
// must not pay for it.
const NewWorkspace = React.lazy(() => import('./design/NewWorkspace'));

/**
 * /new/* -> /dashboard/* , preserving the sub-path and the query string.
 *
 * The redesign lived at /new for its whole review period, so those URLs are in
 * browser history, in bookmarks and in messages. They must not 404.
 */
function LegacyNewRedirect() {
  const { pathname, search } = useLocation();
  const rest = pathname.replace(/^\/new/, '');
  return <Navigate to={`/dashboard${rest}${search}`} replace />;
}

function App() {
  return (
    <Router>
      <Toaster 
        position="top-center" 
        toastOptions={{
          style: {
            background: '#1f2937',
            color: '#fff',
            borderRadius: '12px',
          },
        }}
      />
      <Routes>
        {/*
          The workspace owns /dashboard and everything under it. It MUST be a
          splat: the redesign uses real routes (/dashboard/page, /analytics, …)
          where the original was a single page driven by ?tab=, so an exact path
          here would 404 every screen but the first.
        */}
        <Route
          path="/dashboard/*"
          element={
            <React.Suspense fallback={null}>
              <NewWorkspace />
            </React.Suspense>
          }
        />
        {/*
          The original dashboard, kept mounted so it can be opened and compared
          against. Static, so it outranks the /:username route below.

          THE ROLLBACK IS NOT THIS ROUTE — it is repointing /dashboard/* above
          back at <BioDashboard/>. That works precisely because BioDashboard is
          untouched: it hard-codes '/dashboard' for its logins and all three
          sign-outs, and those literals are CORRECT again the moment it owns
          that path. Editing them to say '/dashboard-classic' would make this
          side door nicer at the cost of modifying the one component the
          rollback depends on, which is a bad trade.

          So this alias is a read-only side door with real limits: it needs a
          '?token=' (it has no blob or Supabase-session fallback), and the first
          sign-out or re-auth inside it returns to the new workspace. Useful for
          looking at the old UI; not a place to work.
        */}
        <Route path="/dashboard-classic" element={<BioDashboard />} />
        <Route path="/new/*" element={<LegacyNewRedirect />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/start" element={<StartPage />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/test" element={<EmbedTestPage />} />
        <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
        <Route path="/collab/:username/:serviceId" element={<CollabOrderPage />} />
        <Route path="/:username/shoutout" element={<ShoutOutProfilePage />} />
        <Route path="/:username/message" element={<MessagePage />} />
        <Route path="/:username/support" element={<SupportPage />} />
        <Route path="/:username/privacy" element={<PrivacyPolicy />} />
        <Route path="/:username" element={<BioPage />} />
      </Routes>
    </Router>
  );
}

export default App;

